/**
 * Street View auto-trigger: diving below a low-zoom threshold auto-opens Street
 * View at the map center — but only where Google actually has imagery (bases
 * usually have none, so the dive stays in the photorealistic 3D scene) and only
 * while the unit/user 'street-view-auto' flag is enabled. Climbing back out
 * auto-closes auto-opened panoramas. (Street View also opens explicitly from
 * the selection card.)
 */

import { useEffect, useRef } from 'react';
import { STREET_VIEW_AUTO_FLAG, flagEnabled } from '@vtak/shared';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import { useStreetViewStore } from '@/stores/street-view-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { AUTO_COVERAGE_RADIUS_M, checkStreetViewCoverage } from '@/map/street-view-coverage';
import { decideAutoAction } from './street-view-auto';

/** 3D camera range (m) below which we dive into Street View (stay in 3D until close in). */
const AUTO_OPEN_RANGE_M = 450;

/** 2D zoom at/above which we are effectively at street level. */
const AUTO_OPEN_ZOOM = 19;

/** Poll cadence for the low-zoom check. */
const POLL_MS = 1200;

/** Wire Street View auto-open-on-dive. */
export function useStreetView(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  const wasLow = useRef(false);
  useEffect(() => {
    const id = setInterval(() => autoToggle(mapRef, modeRef, wasLow), POLL_MS);
    return () => clearInterval(id);
  }, []);
}

/** Evaluate one poll tick against the live map + stores. */
function autoToggle(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
  wasLow: React.MutableRefObject<boolean>,
): void {
  const map = mapRef.current;
  if (!map) return;
  const sv = useStreetViewStore.getState();
  const low = isLowZoom(map, modeRef.current);
  const autoEnabled = flagEnabled(
    useWorkspaceStore.getState().workspace?.corePanelFlags,
    STREET_VIEW_AUTO_FLAG,
  );

  const action = decideAutoAction({
    low,
    wasLow: wasLow.current,
    open: sv.open,
    autoOpened: sv.autoOpened,
    autoEnabled,
  });
  if (action === 'check-open') {
    const center = mapCenter(map, modeRef.current);
    if (center) void gateAndOpen(center, mapRef, modeRef);
  } else if (action === 'close') {
    sv.close();
  }
  wasLow.current = low;
}

/** Open only if imagery exists underfoot and the camera is still down there when the check lands. */
async function gateAndOpen(
  center: { lat: number; lon: number },
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): Promise<void> {
  const hit = await checkStreetViewCoverage(center.lat, center.lon, AUTO_COVERAGE_RADIUS_M);
  if (!hit) return;
  const sv = useStreetViewStore.getState();
  const map = mapRef.current;
  if (sv.open || !map || !isLowZoom(map, modeRef.current)) return;
  sv.openAt({ lat: hit.lat, lon: hit.lon, panoId: hit.panoId }, true);
}

/** Whether the camera is at street level for the current mode. */
function isLowZoom(map: MapInstance, mode: ViewMode): boolean {
  if (mode === '2d') {
    const zoom = (map as google.maps.Map).getZoom();
    return typeof zoom === 'number' && zoom >= AUTO_OPEN_ZOOM;
  }
  const range = (map as google.maps.maps3d.Map3DElement).range;
  return typeof range === 'number' && range > 0 && range < AUTO_OPEN_RANGE_M;
}

/** Current map center for the active mode. */
function mapCenter(map: MapInstance, mode: ViewMode): { lat: number; lon: number } | null {
  if (mode === '2d') {
    const center = (map as google.maps.Map).getCenter();
    return center ? { lat: center.lat(), lon: center.lng() } : null;
  }
  const center = (map as google.maps.maps3d.Map3DElement).center as { lat: number; lng: number } | null;
  return center ? { lat: center.lat, lon: center.lng } : null;
}
